import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axiosInstanceExternal from "../../utils/axiosInstanceExternal";
import Fuse from "fuse.js";
import Select from "react-select";
import { toast } from "react-toastify";
import ReactModal from "react-modal";

import MonthChart from "../../components/Dashboard/MonthChart";
import DayChart from "../../components/Dashboard/DayChart";
import ContactsTable from "../../components/Dashboard/ContactsTable";

const OP_OPTIONS = [
    { value: "eq", label: "Equals" },
    { value: "ne", label: "Does not equal" },
    { value: "has", label: "Contains data" },
    { value: "not_has", label: "Does not contain data" },
    { value: "contains", label: "Contains" },
    { value: "not_contains", label: "Does not contain" },
    { value: "begins", label: "Begins with" },
    { value: "not_begins", label: "Does not begin with" },
    { value: "ends", label: "Ends with" },
    { value: "not_ends", label: "Does not end with" },
];

const isNoop = (f) => !f || (f.op === "eq" && (!f.values || f.values.length === 0));
const matchOne = (raw, f) => {
    const s = (raw ?? "").toString().trim().toLowerCase();
    const vals = (f.values || []).map((v) => v.value.toLowerCase());
    switch (f.op) {
        case "has":
            return s.length > 0;
        case "not_has":
            return s.length === 0;
        case "eq":
            return vals.length === 0 ? true : vals.includes(s);
        case "ne":
            return vals.length === 0 ? true : !vals.includes(s);
        case "contains":
            return vals.length === 0 ? true : vals.some((v) => s.includes(v));
        case "not_contains":
            return vals.length === 0 ? true : vals.every((v) => !s.includes(v));
        case "begins":
            return vals.length === 0 ? true : vals.some((v) => s.startsWith(v));
        case "not_begins":
            return vals.length === 0 ? true : vals.every((v) => !s.startsWith(v));
        case "ends":
            return vals.length === 0 ? true : vals.some((v) => s.endsWith(v));
        case "not_ends":
            return vals.length === 0 ? true : vals.every((v) => !s.endsWith(v));
        default:
            return true;
    }
};
const applyFilter = (rows, field, f) => (isNoop(f) ? rows : rows.filter((r) => matchOne(r[field], f)));
const getLocalYmd = (value) => {
    if (!value) return "";
    const s = String(value).trim();
    const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
};

export default function DashboardContact() {
    const externalAccessToken = localStorage.getItem("externalAccessToken");

    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);

    // charts state
    const [selectedMonth, setSelectedMonth] = useState(null); // YYYY-MM
    const [selectedDay, setSelectedDay] = useState(null); // YYYY-MM-DD

    // table state
    const [currentPage, setCurrentPage] = useState(0);
    const contactsPerPage = 30;

    // filters live with table
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState({ op: "eq", values: [] });
    const [aiPositionFilter, setAiPositionFilter] = useState({ op: "eq", values: [] });

    // sort
    const [sortField, setSortField] = useState("created_at");
    const [sortOrder, setSortOrder] = useState("desc");

    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [contactToDelete, setContactToDelete] = useState(null);
    const [contactToUpdate, setContactToUpdate] = useState({});
    const [contactDetails, setContactDetails] = useState({});

    // action buttons
    const [emailBtnDisabled, setEmailBtnDisabled] = useState(false);
    const [addToQueueBtnDisabled, setAddToQueueBtnDisabled] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const userEmail = localStorage.getItem("email");

    const fetchContacts = useCallback(async () => {
        setIsRefreshing(true);
        try {
            const res = await axiosInstanceExternal.get(`/external/contacts/${encodeURIComponent(externalAccessToken)}`);
            setContacts(res.data || []);
        } catch {
            toast.error("Error fetching contacts.");
            setContacts([]);
        } finally {
            setIsRefreshing(false);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);

    // options
    const statusOptions = useMemo(() => {
        const uniq = Array.from(new Set(contacts.map((c) => c.status).filter(Boolean)));
        return uniq.map((s) => ({ value: s, label: s }));
    }, [contacts]);

    const aiPositionOptions = useMemo(() => {
        const uniq = Array.from(
            new Set(
                contacts.map((c) => {
                    const v = c.ai_position && String(c.ai_position).trim();
                    return v && v.length ? v : "N/A";
                })
            )
        );
        return uniq.map((s) => ({ value: s, label: s }));
    }, [contacts]);

    // search
    const fuse = useMemo(
        () =>
            new Fuse(contacts, {
                keys: ["contact_name", "company_name", "position", "ai_position"],
                threshold: 0.1,
                ignoreLocation: true,
            }),
        [contacts]
    );

    // base filtered by table filters
    const baseFiltered = useMemo(() => {
        let temp = searchTerm.trim() ? fuse.search(searchTerm).map((r) => r.item) : [...contacts];

        temp = applyFilter(temp, "status", statusFilter);
        temp = applyFilter(temp, "ai_position", aiPositionFilter);

        return temp;
    }, [contacts, searchTerm, statusFilter, aiPositionFilter, fuse]);

    // temporal selection from charts
    const withTemporal = useMemo(() => {
        let temp = [...baseFiltered];
        if (selectedDay) {
            temp = temp.filter((c) => getLocalYmd(c.created_at) === selectedDay);
        } else if (selectedMonth) {
            temp = temp.filter((c) => getLocalYmd(c.created_at).substring(0, 7) === selectedMonth);
        }
        return temp;
    }, [baseFiltered, selectedMonth, selectedDay]);

    // sorting
    const tableFiltered = useMemo(() => {
        if (!sortField) return withTemporal;
        const sorted = [...withTemporal].sort((a, b) => {
            const aRaw = a[sortField];
            const bRaw = b[sortField];
            let diff;
            if (sortField === "created_at") {
                diff = new Date(aRaw) - new Date(bRaw);
            } else if (sortField === "follower_count") {
                const toNum = (v) => Number(String(v ?? "").replace(/[,\s]/g, "")) || 0;
                diff = toNum(aRaw) - toNum(bRaw);
            } else {
                diff = (aRaw ?? "").toString().localeCompare((bRaw ?? "").toString(), undefined, { sensitivity: "base" });
            }
            return sortOrder === "asc" ? diff : -diff;
        });
        return sorted;
    }, [withTemporal, sortField, sortOrder]);

    useEffect(
        () => setCurrentPage(0),
        [searchTerm, statusFilter, aiPositionFilter, selectedMonth, selectedDay, sortField, sortOrder]
    );

    // pagination
    const filteredCount = tableFiltered.length;
    const absoluteTotal = contacts.length;
    const pageCount = Math.ceil(filteredCount / contactsPerPage) || 1;
    const displayed = tableFiltered.slice(currentPage * contactsPerPage, (currentPage + 1) * contactsPerPage);

    // charts computed from current table-filtered dataset
    const { labels: monthLabels, counts: monthCounts, monthYAxisMax } = useMemo(() => {
        const start = new Date();
        start.setDate(1);
        start.setMonth(start.getMonth() - 11);
        const months = Array.from({ length: 12 }, (_, i) => {
            const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        });
        const bucket = {};
        for (const c of baseFiltered) {
            const k = getLocalYmd(c.created_at).substring(0, 7);
            if (!months.includes(k)) continue;
            bucket[k] = (bucket[k] || 0) + 1;
        }
        const counts = months.map((m) => bucket[m] || 0);
        const max = counts.length ? Math.max(...counts) : 0;
        return { labels: months, counts, monthYAxisMax: Math.ceil(max / 10) * 10 };
    }, [baseFiltered]);

    const { dayKeys, dayCounts, dayYAxisMax } = useMemo(() => {
        if (!selectedMonth) return { dayKeys: [], dayCounts: [], dayYAxisMax: 0 };
        const [yStr, mStr] = selectedMonth.split("-");
        const y = Number(yStr),
            m = Number(mStr);
        const daysInMonth = new Date(y, m, 0).getDate();
        const keys = Array.from({ length: daysInMonth }, (_, i) => `${selectedMonth}-${String(i + 1).padStart(2, "0")}`);

        const bucket = {};
        for (const r of baseFiltered) {
            const k = getLocalYmd(r.created_at);
            if (k.substring(0, 7) !== selectedMonth) continue;
            bucket[k] = (bucket[k] || 0) + 1;
        }
        const counts = keys.map((k) => bucket[k] || 0);
        const max = counts.length ? Math.max(...counts) : 0;
        return { dayKeys: keys, dayCounts: counts, dayYAxisMax: Math.ceil(max / 10) * 10 };
    }, [baseFiltered, selectedMonth]);

    // actions
    const formatDate = useCallback((dateString) => {
        if (!dateString) return "";
        const dt = new Date(String(dateString).replace(/ GMT$/, ""));
        const day = String(dt.getDate()).padStart(2, "0");
        const mon = dt.toLocaleString("en-AU", { month: "short" });
        const yr = dt.getFullYear();
        const hh = String(dt.getHours()).padStart(2, "0");
        const mm = String(dt.getMinutes()).padStart(2, "0");
        return `${day} ${mon}, ${yr} ${hh}:${mm}`;
    }, []);

    const handleEmailData = async () => {
        if (!tableFiltered.length) {
            toast.info("No contacts to email.");
            return;
        }
        setEmailBtnDisabled(true);
        try {
            await axiosInstanceExternal.post("/contact/send_email", {
                email: userEmail,
                contacts: tableFiltered.map((c) => ({ ...c, created_at: formatDate(c.created_at) })),
            });
            toast.success("Email sent.");
        } catch {
            toast.error("Email failed.");
        } finally {
            setEmailBtnDisabled(false);
        }
    };

    const handleAddToQueue = async () => {
        const unconnected = tableFiltered.filter((c) => c.status === "unconnected");
        const links = [
            ...new Set(
                unconnected.map((c) => (c.profile_url ? String(c.profile_url).trim() : "")).filter(Boolean)
            ),
        ];

        if (!links.length) {
            toast.info("No unconnected contacts with profile links.");
            return;
        }
        setAddToQueueBtnDisabled(true);
        try {
            const { data } = await axiosInstanceExternal.post("/webhook/expandi-campaign-assign", { profile_links: links });
            const okLinks = (data?.results || []).filter((r) => r.ok).map((r) => r.profile_link);
            if (okLinks.length) {
                setContacts((prev) =>
                    prev.map((c) =>
                        okLinks.includes(String(c.profile_url || "").trim()) ? { ...c, status: "requested" } : c
                    )
                );
            }
            toast.success(`Sent ${okLinks.length}/${links.length} to campaign.`);
            await fetchContacts();
        } catch {
            toast.error("Connect queue failed.");
        } finally {
            setAddToQueueBtnDisabled(false);
        }
    };

    const handleAddSingleToQueue = async (contact) => {
        // only unconnected
        if (contact.status !== "unconnected") {
            toast.info("Only unconnected contacts can be added.");
            return;
        }
        const link = contact?.profile_url && String(contact.profile_url).trim();
        if (!link) {
            toast.info("No profile link to send.");
            return;
        }
        setAddToQueueBtnDisabled(true);
        try {
            await axiosInstanceExternal.post("/webhook/expandi-campaign-assign", { profile_link: link });
            toast.success("Sent to campaign.");
            // optimistic update
            setContacts((prev) => prev.map((c) => (c.contact_id === contact.contact_id ? { ...c, status: "requested" } : c)));
            // optional: refresh
            // await fetchContacts();
        } catch {
            toast.error("Add to connect queue failed.");
        } finally {
            setAddToQueueBtnDisabled(false);
        }
    };

    function handleSort(field) {
        if (sortField !== field) {
            setSortField(field);
            setSortOrder("asc");
        } else {
            if (sortOrder === "asc") setSortOrder("desc");
            else if (sortOrder === "desc") setSortField(null);
        }
    }

    if (loading) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-gray-100">
                <div className="flex flex-col items-center gap-4">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-slate-900" />
                    <p className="text-sm sm:text-base font-semibold text-slate-700">Loading contact data…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-full flex flex-col gap-5 sm:gap-6 mt-6">
            {/* Charts */}
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="p-4 sm:p-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                        <div>
                            <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight">Contact Activity</h2>
                            <p className="mt-1 text-sm text-slate-600">Drill down by month/day.</p>
                        </div>
                    </div>

                    <div className="flex justify-center px-1 sm:px-4">
                        <MonthChart
                            labels={monthLabels}
                            counts={monthCounts}
                            minRel={0}
                            yAxisMax={monthYAxisMax}
                            onSelect={(m) => {
                                setSelectedMonth(m);
                                setSelectedDay(null);
                                setCurrentPage(0);
                                if (m) toast.info(`Zoomed into ${m}`);
                            }}
                        />
                    </div>

                    {(selectedMonth || selectedDay) && (
                        <div className="flex flex-wrap items-center gap-2 mt-4">
                            {selectedMonth && (
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-sm font-semibold">
                                    Month: {selectedMonth}
                                    <button
                                        onClick={() => {
                                            setSelectedMonth(null);
                                            setSelectedDay(null);
                                            setCurrentPage(0);
                                        }}
                                        className="ml-1 rounded-full px-2 py-0.5 bg-white/10 hover:bg-white/20 transition"
                                        title="Clear month"
                                        type="button"
                                    >
                                        ✕
                                    </button>
                                </span>
                            )}

                            {selectedDay && (
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-600 text-white text-sm font-semibold">
                                    Day: {selectedDay}
                                    <button
                                        onClick={() => {
                                            setSelectedDay(null);
                                            setCurrentPage(0);
                                        }}
                                        className="ml-1 rounded-full px-2 py-0.5 bg-white/10 hover:bg-white/20 transition"
                                        title="Clear day"
                                        type="button"
                                    >
                                        ✕
                                    </button>
                                </span>
                            )}
                        </div>
                    )}

                    {selectedMonth && (
                        <div className="mt-6 flex justify-center px-1 sm:px-4">
                            <DayChart
                                month={selectedMonth}
                                fullDayKeys={dayKeys}
                                counts={dayCounts}
                                minRel={0}
                                yAxisMax={dayYAxisMax}
                                onSelect={(dayKey) => {
                                    setSelectedDay(dayKey);
                                    setCurrentPage(0);
                                    if (dayKey) toast.info(`Showing ${dayKey}`);
                                }}
                            />
                        </div>
                    )}
                </div>
            </section>

            {/* Actions + Filters + Table */}
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="p-4 sm:p-6">
                    {/* Filters (live with table) */}
                    <div className="mb-4 flex flex-wrap items-end gap-2 sm:gap-3 lg:gap-4">
                        <div className="flex-1 min-w-[260px] w-full">
                            <label className="block text-xs font-semibold text-slate-600 mb-1">Search</label>
                            <input
                                type="text"
                                placeholder="Search name, company, position…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm sm:text-base text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-slate-400/40 focus:border-slate-300 transition"
                            />
                        </div>

                        <FilterPopover btnText="Status" filter={statusFilter} setFilter={setStatusFilter} options={statusOptions} />
                        <FilterPopover
                            btnText="AI Position"
                            filter={aiPositionFilter}
                            setFilter={setAiPositionFilter}
                            options={aiPositionOptions}
                        />
                    </div>

                    {/* Table */}
                    <ContactsTable
                        records={displayed}
                        filteredCount={filteredCount}
                        absoluteTotal={absoluteTotal}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        formatDate={formatDate}
                        onOpenDetails={(rec) => {
                            setContactDetails(rec);
                            setIsDetailsModalOpen(true);
                        }}
                        onOpenUpdate={(rec) => {
                            setContactToUpdate(rec);
                            setIsUpdateModalOpen(true);
                        }}
                        onOpenDelete={(rec) => {
                            setContactToDelete(rec);
                            setIsDeleteModalOpen(true);
                        }}
                        onAddSingleToQueue={handleAddSingleToQueue}
                        addToQueueDisabled={addToQueueBtnDisabled}
                        pageCount={pageCount}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        recordsPerPage={contactsPerPage}
                        actionHidden={true}
                    />
                </div>
            </section>

            {/* Update Modal */}
            <ReactModal
                isOpen={isUpdateModalOpen}
                onRequestClose={() => setIsUpdateModalOpen(false)}
                className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(40rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto outline-none border border-slate-200"
                overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
            >
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Update Contact</h2>
                        <p className="text-sm text-slate-600 mt-1">Edit fields (read-only in this modal).</p>
                    </div>
                    <button
                        onClick={() => setIsUpdateModalOpen(false)}
                        className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                        aria-label="Close"
                        type="button"
                    >
                        ✕
                    </button>
                </div>

                {renderFields(contactToUpdate, true)}

                <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                    <button
                        onClick={() => setIsUpdateModalOpen(false)}
                        className="py-2 px-4 rounded-xl bg-slate-100 font-semibold text-slate-800 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                        type="button"
                    >
                        Close
                    </button>
                </div>
            </ReactModal>

            {/* Details Modal */}
            <ReactModal
                isOpen={isDetailsModalOpen}
                onRequestClose={() => setIsDetailsModalOpen(false)}
                className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(44rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto outline-none border border-slate-200"
                overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
            >
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Contact Details</h2>
                        <p className="text-sm text-slate-600 mt-1">Read-only view of the contact.</p>
                    </div>
                    <button
                        onClick={() => setIsDetailsModalOpen(false)}
                        className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                        aria-label="Close"
                        type="button"
                    >
                        ✕
                    </button>
                </div>

                {renderFields(contactDetails, false, true)}
            </ReactModal>

            {/* Delete Modal */}
            <ReactModal
                isOpen={isDeleteModalOpen}
                onRequestClose={() => setIsDeleteModalOpen(false)}
                className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(28rem,calc(100vw-2rem))] outline-none border border-slate-200"
                overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
            >
                <div className="flex items-start justify-between gap-3 mb-3">
                    <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Confirm Deletion</h2>
                    <button
                        onClick={() => setIsDeleteModalOpen(false)}
                        className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                        aria-label="Close"
                        type="button"
                    >
                        ✕
                    </button>
                </div>

                <p className="text-slate-700 mb-5 text-sm sm:text-base">Are you sure you want to delete this contact?</p>

                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                    <button
                        onClick={() => setIsDeleteModalOpen(false)}
                        className="py-2 px-4 rounded-xl bg-slate-100 text-slate-800 font-semibold hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                        type="button"
                    >
                        Cancel
                    </button>
                </div>
            </ReactModal>
        </div>
    );

    function renderFields(record, updateMode, readOnly = false) {
        const fields = [
            { label: "Contact Name", name: "contact_name" },
            { label: "Company Name", name: "company_name" },
            { label: "Position", name: "position" },
            { label: "AI Position", name: "ai_position" },
            { label: "LinkedIn Email", name: "email" },
            { label: "Business Email", name: "business_email" },
            { label: "Phone", name: "phone" },
            { label: "Profile URL", name: "profile_url", isLink: true },
            { label: "Follower Count", name: "follower_count" },
            { label: "Company Website", name: "company_website", isLink: true },
            { label: "Company LinkedIn", name: "company_linkedin", isLink: true },
            { label: "Sales Nav Link", name: "sales_nav_link", isLink: true },
            { label: "Country", name: "country" },
            { label: "Status", name: "status" },
            { label: "Created At", name: "created_at", isDate: true, readOnly: true },
        ];

        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {fields.map((field) => {
                    let value = record[field.name] ?? "";
                    if (field.isDate) value = formatDate(value);

                    if (readOnly && field.isLink && value) {
                        return (
                            <div key={field.name} className="sm:col-span-2">
                                <label className="block text-xs font-semibold text-slate-600 mb-1">{field.label}</label>
                                <a
                                    href={value.startsWith("http") ? value : `https://${value}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full break-all rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
                                >
                                    {value}
                                </a>
                            </div>
                        );
                    }

                    return (
                        <div key={field.name} className={field.name === "sales_nav_link" ? "sm:col-span-2" : ""}>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">{field.label}</label>
                            <input
                                name={field.name}
                                type="text"
                                value={value}
                                onChange={(e) =>
                                    !readOnly && (updateMode ? setContactToUpdate((p) => ({ ...p, [field.name]: e.target.value })) : null)
                                }
                                readOnly={readOnly || field.readOnly}
                                className={[
                                    "w-full rounded-xl border px-3 py-2 text-sm text-slate-900 transition",
                                    readOnly || field.readOnly
                                        ? "border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed"
                                        : "border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/40 focus:border-slate-300",
                                ].join(" ")}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }
}

function FilterPopover({ btnText, filter, setFilter, options }) {
    const [open, setOpen] = useState(false);
    const [draftOp, setDraftOp] = useState(filter.op);
    const [draftVals, setDraftVals] = useState(filter.values || []);
    const ref = useRef(null);

    useEffect(() => {
        if (open) {
            setDraftOp(filter.op);
            setDraftVals(filter.values || []);
        }
    }, [open, filter.op, filter.values]);

    useEffect(() => {
        const onDoc = (e) => {
            if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const disabled = draftOp === "has" || draftOp === "not_has";

    return (
        <div className="relative">
            <button
                type="button"
                className="h-11 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60"
                onClick={() => setOpen((o) => !o)}
            >
                Filter: {btnText}
                <span className="ml-2 text-slate-400" aria-hidden="true">
                    ▾
                </span>
            </button>

            {open && (
                <div
                    ref={ref}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="absolute right-0 mt-2 w-80 rounded-2xl bg-white border border-slate-200 shadow-xl p-4 z-20"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="font-extrabold text-slate-900">Filter by {btnText}</div>
                        <button
                            className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                            onClick={() => setOpen(false)}
                            type="button"
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="space-y-3">
                        <Select
                            value={OP_OPTIONS.find((o) => o.value === draftOp)}
                            onChange={(op) => setDraftOp(op.value)}
                            options={OP_OPTIONS}
                            classNamePrefix="react-select"
                            placeholder="Equals"
                            styles={{
                                control: (p) => ({
                                    ...p,
                                    minHeight: 44,
                                    height: 44,
                                    borderColor: "#e2e8f0",
                                    boxShadow: "none",
                                    borderRadius: 12,
                                    "&:hover": { borderColor: "#cbd5e1" },
                                }),
                                option: (p, s) => ({
                                    ...p,
                                    backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
                                    color: s.isSelected ? "white" : "#0f172a",
                                }),
                                menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
                            }}
                        />

                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isDisabled={disabled}
                            value={draftVals}
                            onChange={(vals) => setDraftVals(vals || [])}
                            options={options}
                            classNamePrefix="react-select"
                            placeholder="Select…"
                            styles={{
                                control: (p) => ({
                                    ...p,
                                    minHeight: 44,
                                    borderColor: "#e2e8f0",
                                    boxShadow: "none",
                                    borderRadius: 12,
                                    "&:hover": { borderColor: "#cbd5e1" },
                                    opacity: disabled ? 0.65 : 1,
                                }),
                                option: (p, s) => ({
                                    ...p,
                                    backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
                                    color: s.isSelected ? "white" : "#0f172a",
                                }),
                                menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
                            }}
                        />
                    </div>

                    <div className="mt-4 flex justify-end gap-2">
                        <button
                            className="px-3 py-2 text-sm font-semibold rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 transition focus:outline-none focus:ring-2 focus:ring-slate-300"
                            onClick={() => setDraftVals([])}
                            type="button"
                        >
                            Clear
                        </button>
                        <button
                            className="px-4 py-2 text-sm font-semibold rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                            onClick={() => {
                                setFilter({ op: draftOp, values: draftVals });
                                setOpen(false);
                            }}
                            type="button"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
